<template>
  <v-breadcrumbs
    :items="visibleBreadcrumbs"
    class="breadcrumbs-container"
  >
    <template #item="{ item }">
      <drop-down-menu
        v-if="shouldDisplayDropdown && item.title === 'dropdown'"
        :menu-items="hiddenBreadcrumbs"
        :icon-size="32"
        custom-toggler
        enable-indentation
        :nudge-right="-185"
        @select="(value) => onBreadcrumbClick(value, item)"
      >
        <template #toggler="{ on, attrs }">
          <button-common
            icon
            :icon-size="24"
            class="breadcrumbs__dropdown__button"
            append-icon="$icon_more_vert"
            v-bind="attrs"
            type="clear"
            v-on="on"
          />
        </template>
      </drop-down-menu>
      <v-breadcrumbs-item
        v-else
        :class="[
          'breadcrumb',
          item.breadcrumbIndex === (indexedBreadcrumbs && indexedBreadcrumbs.length - 1) && 'breadcrumb--last',
        ]"
        :data-testid="item.breadcrumbIndex"
        @click="onBreadcrumbClick(item.breadcrumbIndex)"
      >
        <tooltipped-text :text="item.title" />
      </v-breadcrumbs-item>
    </template>
    <template #divider>
      <v-icon> {{ dividerIcon ? dividerIcon : '$icon_arrow_right' }} </v-icon>
    </template>
  </v-breadcrumbs>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, onUnmounted } from 'vue';
import TooltippedText from '../tooltip/TooltippedText/TooltippedText.vue';
import ButtonCommon from '../buttons/ButtonCommon.vue';
import DropDownMenu from '../dropdowns/DropDownMenu.vue';

export type breadcrumb = {
  title: string;
  onClick?: () => void;
};

interface indexedBreadcrumb extends breadcrumb {
  breadcrumbIndex?: number;
}

interface indexedHiddenBreadcrumb extends indexedBreadcrumb {
  name: string;
  value?: number;
}

const emit = defineEmits<{
  (e: 'input', value?: breadcrumb[]): void;
}>();

const props = defineProps<{
  value?: breadcrumb[];
  dividerIcon?: string;
  parentClass?: string;
}>();

const DROPDOWN_AND_SEPARATOR_WIDTH = 200;
const BREADCRUMBS_CONTAINER_CLASS = 'breadcrumbs-container';

const hiddenBreadcrumbs = ref<indexedHiddenBreadcrumb[]>([]);
const visibleBreadcrumbs = ref<indexedBreadcrumb[]>([]);
let spacerElement = document.getElementsByClassName(props.parentClass || BREADCRUMBS_CONTAINER_CLASS)[0];

const resizeObserver = new ResizeObserver(() => {
  createBreadcrumbs(props.value ?? []);
});

async function createBreadcrumbs(breadcrumbs: breadcrumb[]) {
  hiddenBreadcrumbs.value = [];
  prepareBreadcrumbsStructure(breadcrumbs);
  await nextTick();
  await moveItemsToDropdown();

  hiddenBreadcrumbs.value.length &&
    visibleBreadcrumbs.value.splice(1, 0, {
      title: 'dropdown',
    });
}

const shouldDisplayDropdown = computed(() => {
  return hiddenBreadcrumbs.value?.length;
});

async function moveItemsToDropdown() {
  const breadcrumbsChildren = document.getElementsByClassName(BREADCRUMBS_CONTAINER_CLASS)[0]?.children || [];

  if (visibleBreadcrumbs.value.length <= 1 || !breadcrumbsChildren) {
    return;
  }

  let allChildrenWidth = 0;

  for (const item of breadcrumbsChildren) {
    allChildrenWidth += item.clientWidth;
  }

  if (allChildrenWidth && allChildrenWidth > spacerElement.clientWidth - DROPDOWN_AND_SEPARATOR_WIDTH) {
    const movedItem: indexedBreadcrumb = visibleBreadcrumbs?.value?.splice(1, 1)[0];

    movedItem &&
      hiddenBreadcrumbs.value.push({
        ...movedItem,
        name: movedItem.title,
        value: movedItem.breadcrumbIndex,
      });

    await nextTick();
    await moveItemsToDropdown();
  }
}

function prepareBreadcrumbsStructure(breadcrumbs: breadcrumb[]) {
  const displayed: indexedBreadcrumb[] = [];

  breadcrumbs.forEach(async (breadcrumb: breadcrumb, index: number) => {
    displayed.push({
      ...breadcrumb,
      breadcrumbIndex: index,
    });
  });

  visibleBreadcrumbs.value = displayed;
}

const indexedBreadcrumbs = computed({
  get: () => {
    createBreadcrumbs(props.value ?? []);
    // Appending a breadcrumbIndex key
    return (
      props.value &&
      props.value.map((breadcrumb: breadcrumb, index) => {
        return {
          ...breadcrumb,
          breadcrumbIndex: index,
        };
      })
    );
  },
  set: (newBreadcrumbNavigation?: indexedBreadcrumb[]) => {
    emit(
      'input',
      newBreadcrumbNavigation &&
        newBreadcrumbNavigation.map(function (item) {
          delete item.breadcrumbIndex;
          return item;
        })
    );
  },
});

function onBreadcrumbClick(index: number) {
  if (props.value && index < props.value.length - 1) {
    const updatedBreadcrumbNavigation =
      indexedBreadcrumbs.value &&
      indexedBreadcrumbs.value.filter((breadcrumb: indexedBreadcrumb) => breadcrumb.breadcrumbIndex! <= index);
    indexedBreadcrumbs.value = updatedBreadcrumbNavigation;
  }

  if (props.value && props.value[index] && props.value[index].onClick) {
    props.value[index].onClick();
  }
}

onMounted(() => {
  spacerElement = document.getElementsByClassName(props.parentClass || BREADCRUMBS_CONTAINER_CLASS)[0];

  if (props.value) {
    createBreadcrumbs(props.value);
  }
  resizeObserver.observe(spacerElement);
});

onUnmounted(() => {
  resizeObserver.unobserve(spacerElement);
});
</script>

<style lang="scss" scoped>
@import '../../assets/styles/main';

ol.breadcrumbs-container,
ul.breadcrumbs-container {
  padding: 0 !important;
}

.breadcrumbs__dropdown__button {
  transform: rotate(90deg);
}

::v-deep .breadcrumb {
  max-width: 196px;
  .v-breadcrumbs__item {
    @include subtitle-1;
    color: $secondary;
    cursor: pointer;
    width: 100%;
  }

  &:hover {
    .v-breadcrumbs__item {
      color: $primary;
    }
  }

  &--last {
    .v-breadcrumbs__item {
      color: $primary;
    }
  }
}
</style>
